import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  OnDestroy,
} from '@angular/core';
import * as echarts from 'echarts/core';
import {
  type GraphTooltipParams,
  InsightsDataService,
} from '../../insights-data.service';
import { Subject, takeUntil } from 'rxjs';
import { partialIntervalGraph } from '../../insights-data.interface';
import { AccessibilityService } from '../../../../../../core/services/accessibility.service';

@Component({
  selector: 'app-partial-interval-graph',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './partial-interval-graph.component.html',
  styleUrl: './partial-interval-graph.component.scss',
})
export class PartialIntervalGraphComponent implements OnDestroy {
  showTrendLine = false;
  chartInstance!: echarts.ECharts;
  chartInstances: echarts.ECharts[] = [];
  weekGraphData: partialIntervalGraph[] | null = null;
  renderedChartsCount = 0;
  allChartsRendered = false;
  renderedChartsCountTotal = 0;
  allChartsRenderedTotal = false;
  private readonly destroy$ = new Subject<void>();
  totalCharts = 0;
  isShow = true;
  maxLength = 15;
  splitRange = 15;
  behaviorId = 0;
  private readonly printTextColor = '#000000';
  private currentGraphData: partialIntervalGraph | null = null;
  images: string[] = [];
  @Output() chartImage = new EventEmitter<string>();
  @Output() allChartImage = new EventEmitter<string[]>();
  @Output() allChartTotalImage = new EventEmitter<string[]>();
  chartInstanceTotal!: echarts.ECharts;
  chartsInstanceTotal: echarts.ECharts[] = [];
  @Output() chartTotalImage = new EventEmitter<string>();
  @Input() set graphData(partialIntervalData: partialIntervalGraph) {
    this.currentGraphData = partialIntervalData;
    this.behaviorId = partialIntervalData.caseBehaviorId ?? 0;
    this.showTrendLine = partialIntervalData.showTrendLine ?? false;
    this.renderMainCharts(partialIntervalData);
    this.renderPrintCharts(partialIntervalData);
  }

  constructor(
    private readonly elementRef: ElementRef,
    private readonly insightsService: InsightsDataService,
    private readonly accessibilityService: AccessibilityService
  ) {
    this.accessibilityService.contrastMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.applyThemeToExistingCharts();
      });
  }

  setGraphOptionsDefault(
    partialIntervalData: partialIntervalGraph,
    showDaysInterval: boolean
  ) {
    if (showDaysInterval) {
      this.renderPrintCharts(partialIntervalData);
      return;
    }

    this.renderMainCharts(partialIntervalData);
  }

  private renderPrintCharts(partialIntervalData: partialIntervalGraph): void {
    this.images = [];
    this.chartInstances = [];
    this.chartsInstanceTotal = [];
    this.renderedChartsCount = 0;
    this.renderedChartsCountTotal = 0;
    this.totalCharts = 1;
    this.allChartsRendered = false;
    this.allChartsRenderedTotal = false;
    this.weekGraphData = [];
    const weekData = partialIntervalData;
    const allDataNull =
      weekData.interval1Percent?.every(value => value === null) &&
      weekData.interval2Percent?.every(value => value === null) &&
      weekData.interval3Percent?.every(value => value === null) &&
      weekData.partialIntervalAveragePercent.every(value => value === null);

    this.weekGraphData.push(weekData);
    const { partialIntervalGraphOption, partialIntervalTotalGraphOption } =
      this.setGraphOptions(weekData, true);
    setTimeout(() => {
      const chartDom = document.getElementById(
        `chart-container-partial-${this.behaviorId}-${0}`
      );
      const chartTotalDom = document.getElementById(
        `chart-container-partial-total-${this.behaviorId}-${0}`
      );
      if (chartDom) {
        const chartInstance = echarts.init(chartDom, null, {
          renderer: 'canvas',
        });
        chartInstance.setOption(partialIntervalGraphOption);
        chartInstance.resize();

        chartInstance.on('finished', () => {
          this.renderedChartsCount++;

          if (this.renderedChartsCount === this.totalCharts) {
            this.allChartsRendered = true;
            this.exportAllGraphs();
          }
        });

        if (!allDataNull) {
          this.chartInstances.push(chartInstance);
        }
      }
      if (chartTotalDom) {
        const chartInstanceTotal = echarts.init(chartTotalDom, null, {
          renderer: 'canvas',
        });
        chartInstanceTotal.setOption(partialIntervalTotalGraphOption);
        chartInstanceTotal.resize();

        chartInstanceTotal.on('finished', () => {
          this.renderedChartsCountTotal++;
          if (this.renderedChartsCountTotal === this.totalCharts) {
            this.allChartsRenderedTotal = true;
            this.exportAllGraphsTotal();
          }
        });
        if (!allDataNull) {
          this.chartsInstanceTotal.push(chartInstanceTotal);
        }
      }
    }, 500);
  }

  private renderMainCharts(partialIntervalData: partialIntervalGraph): void {
    const { partialIntervalGraphOption, partialIntervalTotalGraphOption } =
      this.setGraphOptions(partialIntervalData);
    const partialIntervalDom =
      this.elementRef.nativeElement.querySelector('#partial-interval');
    const partialIntervalTotalDom = this.elementRef.nativeElement.querySelector(
      '#partial-interval-total'
    );
    if (partialIntervalDom) {
      const chartInstance = echarts.init(partialIntervalDom, null, {
        renderer: 'canvas',
      });
      this.chartInstance = chartInstance;
      chartInstance.setOption(partialIntervalGraphOption);
      chartInstance.on('finished', () => {
        this.emitChartAsImage();
      });
    }
    if (partialIntervalTotalDom) {
      const chartInstance = echarts.init(partialIntervalTotalDom, null, {
        renderer: 'canvas',
      });
      this.chartInstanceTotal = chartInstance;
      chartInstance.setOption(partialIntervalTotalGraphOption);
      chartInstance.on('finished', () => {
        this.emitChartAsImageTotal();
      });
    }
  }

  setGraphOptions(partialInterval: partialIntervalGraph, forPrint = false) {
    const textColor = forPrint
      ? this.printTextColor
      : this.getThemeChartTextColor();
    const dataZoomConfig = [
      {
        type: 'inside',
        xAxisIndex: 0,
        zoomOnMouseWheel: true,
        moveOnMouseMove: true,
        moveOnMouseWheel: true,
        minValueSpan: null,
        maxValueSpan: null,
        start: 0,
        end: 100,
      },
    ];

    const trendlineInterval1 = this.insightsService.generateTrendlineArray(
      partialInterval.xLabels,
      partialInterval.interval1Percent || []
    );
    const trendlineInterval2 = this.insightsService.generateTrendlineArray(
      partialInterval.xLabels,
      partialInterval.interval2Percent || []
    );
    const trendlineInterval3 = this.insightsService.generateTrendlineArray(
      partialInterval.xLabels,
      partialInterval.interval3Percent || []
    );

    const partialIntervalGraphOption = {
      title: {
        text: 'Partial Interval',
        left: 'center',
        textStyle: {
          color: textColor,
        },
      },
      tooltip: {
        trigger: 'item',
        formatter: (params: GraphTooltipParams) =>
          this.insightsService.formatItemTooltipWithWeekday(params),
      },
      legend: {
        data: [
          'Interval 1',
          'Interval 2',
          'Interval 3',
          'Trendline Interval 1',
          'Trendline Interval 2',
          'Trendline Interval 3',
        ],
        left: 'left',
        bottom: 40,
        textStyle: {
          color: textColor,
        },
        pageTextStyle: {
          color: textColor,
        },
      },
      grid: { left: 45, right: 45, top: 50, bottom: 70, containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: true,
        data: partialInterval.xLabels,
        axisLabel: {
          color: textColor,
        },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: true },
        axisLabel: {
          formatter: '{value}%',
          color: textColor,
        },
        nameLocation: 'center',
        nameTextStyle: {
          padding: [0, 0, 8, 0],
          color: textColor,
        },
      },
      series: [
        {
          name: 'Interval 1',
          type: 'line',
          data: partialInterval.interval1Percent,
          symbolSize: 10,
          label: {
            show: true,
            position: 'top',
            color: 'blue',
            fontSize: 12,
            formatter: '{c}%',
          },
          connectNulls: true,
          showSymbol: true,
          showAllSymbol: true,
        },
        {
          name: 'Interval 2',
          type: 'line',
          data: partialInterval.interval2Percent,
          symbolSize: 10,
          label: {
            show: true,
            position: 'left',
            color: '#47CD89',
            fontSize: 12,
            formatter: '{c}%',
          },
          connectNulls: true,
          showSymbol: true,
          showAllSymbol: true,
        },
        {
          name: 'Interval 3',
          type: 'line',
          data: partialInterval.interval3Percent,
          symbolSize: 10,
          label: {
            show: true,
            position: 'right',
            color: 'orange',
            fontSize: 12,
            formatter: '{c}%',
          },
          connectNulls: true,
          showSymbol: true,
          showAllSymbol: true,
        },
        ...(this.showTrendLine
          ? [
              {
                name: 'Trendline Interval 1',
                type: 'line',
                data: trendlineInterval1,
                symbol: 'none',
                lineStyle: {
                  color: 'blue',
                  width: 2,
                  type: 'dashed',
                },
                emphasis: { disabled: true },
                showSymbol: false,
                connectNulls: true,
                z: 10,
              },
              {
                name: 'Trendline Interval 2',
                type: 'line',
                data: trendlineInterval2,
                symbol: 'none',
                lineStyle: {
                  color: '#47CD89',
                  width: 2,
                  type: 'dashed',
                },
                emphasis: { disabled: true },
                showSymbol: false,
                connectNulls: true,
                z: 10,
              },
              {
                name: 'Trendline Interval 3',
                type: 'line',
                data: trendlineInterval3,
                symbol: 'none',
                lineStyle: {
                  color: 'orange',
                  width: 2,
                  type: 'dashed',
                },
                emphasis: { disabled: true },
                showSymbol: false,
                connectNulls: true,
                z: 10,
              },
            ]
          : []),
      ],
      dataZoom: dataZoomConfig,
    };

    const trendlineAverage = this.insightsService.generateTrendlineArray(
      partialInterval.xLabels,
      partialInterval.partialIntervalAveragePercent
    );

    const partialIntervalTotalGraphOption = {
      title: {
        text: 'Partial Interval Average',
        left: 'center',
        textStyle: {
          color: textColor,
        },
      },
      tooltip: {
        trigger: 'item',
        formatter: (params: GraphTooltipParams) =>
          this.insightsService.formatItemTooltipWithWeekday(params),
      },
      legend: {
        data: ['Partial Interval Average', 'Trendline Average'],
        left: 'left',
        bottom: 40,
        textStyle: {
          color: textColor,
        },
        pageTextStyle: {
          color: textColor,
        },
      },
      grid: { left: 45, right: 45, top: 50, bottom: 70, containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: true,
        data: partialInterval.xLabels,
        axisLabel: {
          color: textColor,
        },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: true },
        axisLabel: {
          formatter: '{value}%',
          color: textColor,
        },
        nameLocation: 'center',
        nameTextStyle: {
          padding: [0, 0, 8, 0],
          color: textColor,
        },
      },
      series: [
        {
          name: 'Partial Interval Average',
          type: 'line',
          data: partialInterval.partialIntervalAveragePercent,
          symbolSize: 10,
          itemStyle: {
            color: '#47CD89',
          },
          label: {
            show: true,
            position: 'top',
            color: '#47CD89',
            fontSize: 12,
            formatter: '{c}%',
          },
          connectNulls: true,
          showSymbol: true,
          showAllSymbol: true,
        },
        ...(this.showTrendLine
          ? [
              {
                name: 'Trendline Average',
                type: 'line',
                data: trendlineAverage,
                symbol: 'none',
                lineStyle: {
                  color: 'red',
                  width: 2,
                  type: 'dashed',
                },
                emphasis: { disabled: true },
                showSymbol: false,
                connectNulls: true,
                z: 10,
              },
            ]
          : []),
      ],
      dataZoom: dataZoomConfig,
    };

    return { partialIntervalGraphOption, partialIntervalTotalGraphOption };
  }

  private getThemeChartTextColor(): string {
    const colorValue = getComputedStyle(document.documentElement)
      .getPropertyValue('--gray-600')
      .trim();

    return colorValue ? `rgb(${colorValue})` : '#475467';
  }

  private applyThemeToExistingCharts(): void {
    const graphData = this.currentGraphData;
    if (!graphData) {
      return;
    }

    const { partialIntervalGraphOption, partialIntervalTotalGraphOption } =
      this.setGraphOptions(graphData);
    const {
      partialIntervalGraphOption: partialIntervalGraphOptionPrint,
      partialIntervalTotalGraphOption: partialIntervalTotalGraphOptionPrint,
    } = this.setGraphOptions(graphData, true);

    if (this.chartInstance) {
      this.chartInstance.setOption(partialIntervalGraphOption, true);
    }

    if (this.chartInstanceTotal) {
      this.chartInstanceTotal.setOption(partialIntervalTotalGraphOption, true);
    }

    this.chartInstances.forEach(chart => {
      chart.setOption(partialIntervalGraphOptionPrint, true);
    });

    this.chartsInstanceTotal.forEach(chart => {
      chart.setOption(partialIntervalTotalGraphOptionPrint, true);
    });
  }
  emitChartAsImage() {
    const chartImageBase64 = this.insightsService.convertImagetoBase64(
      this.chartInstance
    );
    this.chartImage.emit(chartImageBase64);
  }
  emitChartAsImageTotal() {
    const chartImageBase64 = this.insightsService.convertImagetoBase64(
      this.chartInstanceTotal
    );
    this.chartTotalImage.emit(chartImageBase64);
  }

  exportAllGraphs() {
    if (!this.allChartsRendered) {
      return;
    }

    const images: string[] = [];

    this.chartInstances.forEach(chartInstance => {
      const base64Image =
        this.insightsService.convertImagetoBase64(chartInstance);

      images.push(base64Image);
    });
    this.allChartImage.emit(images);
  }
  exportAllGraphsTotal() {
    if (!this.allChartsRenderedTotal) {
      return;
    }

    const images: string[] = [];

    this.chartsInstanceTotal.forEach(chartInstance => {
      const base64Image =
        this.insightsService.convertImagetoBase64(chartInstance);
      images.push(base64Image);
    });
    this.allChartTotalImage.emit(images);
  }

  ngOnDestroy(): void {
    this.images = [];
    this.destroy$.next();
    this.destroy$.complete();
  }
}
